/* eslint-disable react/jsx-props-no-spreading */
import FormControlLabel from "@mui/material/FormControlLabel"
import type { FormControlLabelProps } from "@mui/material/FormControlLabel"
import { ReactElement } from "react"
import { Control, Controller, UseControllerProps } from "react-hook-form"

import AppSwitch from "../../buttons/switch/appSwitch"

interface IValidatedSwitchProps {
    control: Control
    registerName: string
    label: string
    labelPlacement: FormControlLabelProps["labelPlacement"]
    spaceBetweenLabel?: boolean
    disabled?: boolean
    required?: boolean
    customRules?: UseControllerProps["rules"]
    onToggle?: () => void
}

/**
 * Switch with designed to be controlled by react-hook-form. Must be used
 * in a component with a useForm hook from react-hook-form. Register name
 * and control are exposed from useForm. This is for a simple true/false toggle
 */
const ValidatedSwitch = ({
    registerName,
    control,
    label,
    labelPlacement,
    spaceBetweenLabel = false,
    disabled = false,
    required = false,
    customRules = {},
    onToggle = undefined,
}: IValidatedSwitchProps): ReactElement => {
    const requiredRule = {
        required: { value: true, message: "You must accept this field" },
    }

    const rules = required ? { ...customRules, ...requiredRule } : customRules

    const baseLabelStyling: FormControlLabelProps["sx"] = spaceBetweenLabel
        ? {
              justifyContent: "space-between",
              margin: 0,
              width: "100%",
          }
        : {}

    const errorStyling: FormControlLabelProps["sx"] = {
        ".MuiFormControlLabel-label": {
            color: "error.main",
        },

        ".MuiFormControlLabel-label.Mui-disabled": {
            color: "error.main",
            opacity: "0.38",
        },
    }

    return (
        <Controller
            name={registerName}
            control={control}
            rules={rules}
            render={({ field, fieldState: { error } }) => (
                <FormControlLabel
                    {...field}
                    label={label}
                    labelPlacement={labelPlacement}
                    sx={
                        error
                            ? { ...baseLabelStyling, ...errorStyling }
                            : baseLabelStyling
                    }
                    required={required}
                    disabled={disabled}
                    onChange={(_, checked) => {
                        field.onChange(checked)
                        if (onToggle) {
                            onToggle()
                        }
                    }}
                    control={<AppSwitch checked={field.value} />}
                />
            )}
        />
    )
}

ValidatedSwitch.defaultProps = {
    spaceBetweenLabel: false,
    disabled: false,
    required: false,
    customRules: {},
    onToggle: undefined,
}

export default ValidatedSwitch
